/* ==========================================================================
   Metcos product video widget

   A muted mini reel floats on the product page. Tapping it opens a
   full-screen shoppable player with sound, controls and a buy bar.

   Bandwidth notes (this matters on Pakistani mobile data)
   - The mini card is ~112px wide, so it streams the 480p rendition.
   - The HD rendition is only ever requested when the shopper actually
     opens the player, and it starts from the beginning with sound.
   - Nothing is requested at all until the page has gone idle.
   - On Save-Data or a 2G connection the video never autoplays; the
     shopper gets the poster frame and a play button instead.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.getElementById('MdjVideo');
  if (!root) return;

  var CFG = {};
  try {
    var cfgEl = document.getElementById('MdjVideoConfig');
    if (cfgEl) CFG = JSON.parse(cfgEl.textContent) || {};
  } catch (e) {
    CFG = {};
  }

  // Dismissal is remembered PER PRODUCT, not for the whole visit. Closing
  // the card on one product must not hide it on the next product that has
  // a video. The clip's own URL identifies the product, so it doubles as
  // the key and needs nothing extra from Liquid.
  var DISMISS_KEY = 'mdj-vid-dismissed';

  function dismissedList() {
    try {
      var raw = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]');
      // Older builds stored the string '1' here. Treat anything that is not
      // a list as "nothing dismissed" rather than throwing.
      return Object.prototype.toString.call(raw) === '[object Array]' ? raw : [];
    } catch (e) {
      return [];
    }
  }

  function dismissKey() {
    return CFG.productId || CFG.miniSrc || window.location.pathname;
  }

  function rememberDismissed() {
    try {
      var list = dismissedList();
      if (list.indexOf(dismissKey()) === -1) list.push(dismissKey());
      // A long browse session should not grow this without limit.
      if (list.length > 40) list = list.slice(-40);
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode */
    }
  }

  if (dismissedList().indexOf(dismissKey()) !== -1) return;

  var miniVideo = root.querySelector('[data-mdj-vid-video]');
  var closeBtn = root.querySelector('[data-mdj-vid-close]');

  var fs = document.getElementById('MdjVideoFs');
  var fsVideo = fs && fs.querySelector('[data-mdj-vfs-video]');
  var fsClose = fs && fs.querySelector('[data-mdj-vfs-close]');
  var fsPlay = fs && fs.querySelector('[data-mdj-vfs-play]');
  var fsMute = fs && fs.querySelector('[data-mdj-vfs-mute]');
  var fsAtc = fs && fs.querySelector('[data-mdj-vfs-atc]');
  var fsCount = fs && fs.querySelector('[data-mdj-vfs-count]');
  var fsPrice = fs && fs.querySelector('[data-mdj-vfs-price]');
  var fsCompare = fs && fs.querySelector('[data-mdj-vfs-compare]');

  if (!miniVideo) return;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ------------------------------------------------------------------
  // Connection awareness
  // ------------------------------------------------------------------

  function isFrugalConnection() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || '';
    return t === 'slow-2g' || t === '2g';
  }

  var mayAutoplay = !reduceMotion && !(isFrugalConnection() && CFG.autoplayOnSlow !== true);

  // ------------------------------------------------------------------
  // Mini card
  // ------------------------------------------------------------------

  var miniLoaded = false;

  function loadMini() {
    if (miniLoaded) return;
    miniLoaded = true;
    if (CFG.miniSrc) {
      miniVideo.muted = true;
      miniVideo.src = CFG.miniSrc;
      miniVideo.load();
    }
  }

  function playMini() {
    if (!mayAutoplay) {
      root.classList.add('is-paused');
      return;
    }
    loadMini();
    var p = miniVideo.play();
    if (p && p.catch) {
      p.catch(function () {
        // Autoplay refused (some iOS low-power states). Show the play
        // affordance rather than sitting on a dead black box.
        root.classList.add('is-paused');
      });
    }
  }

  function reveal() {
    root.classList.add('is-ready');
    playMini();
  }

  var delayMs = (CFG.delaySeconds != null ? CFG.delaySeconds : 3) * 1000;

  function schedule() {
    setTimeout(function () {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(reveal, { timeout: 2000 });
      } else {
        reveal();
      }
    }, delayMs);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule);

  // Never leave a video decoding in a background tab.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      miniVideo.pause();
      if (fsVideo) fsVideo.pause();
    } else if (!isOpen) {
      playMini();
    }
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      miniVideo.pause();
      miniVideo.removeAttribute('src');
      miniVideo.load();
      root.classList.remove('is-ready');
      rememberDismissed();
    });
  }

  // Lift the card above the sticky add-to-cart bar when that appears,
  // so the two never sit on top of each other.
  var sticky = document.getElementById('MdjStickyAtc');
  if (sticky && 'MutationObserver' in window) {
    var syncLift = function () {
      var visible = sticky.classList.contains('is-visible');
      root.style.setProperty('--mdj-vid-lift', visible ? sticky.offsetHeight + 10 + 'px' : '0px');
    };
    new MutationObserver(syncLift).observe(sticky, {
      attributes: true,
      attributeFilter: ['class']
    });
    syncLift();
  }

  // ------------------------------------------------------------------
  // Full-screen player
  // ------------------------------------------------------------------

  var isOpen = false;
  var fsLoaded = false;

  function open() {
    if (!fs || isOpen) return;
    isOpen = true;

    miniVideo.pause();

    if (!fsLoaded) {
      fsLoaded = true;
      if (CFG.fullSrc) {
        fsVideo.src = CFG.fullSrc;
        fsVideo.load();
      }
    }

    fs.classList.add('is-open');
    document.body.classList.add('mdj-vfs-open');
    fs.setAttribute('aria-hidden', 'false');

    // Opening is a user gesture, so sound is allowed. Start from the top:
    // the shopper has just chosen to watch it properly.
    fsVideo.currentTime = 0;
    fsVideo.muted = false;
    if (fsMute) fsMute.classList.remove('is-active');
    var p = fsVideo.play();
    if (p && p.catch) {
      p.catch(function () {
        // If sound is still blocked, fall back to muted playback.
        fsVideo.muted = true;
        if (fsMute) fsMute.classList.add('is-active');
        fsVideo.play().catch(function () {});
      });
    }
    if (fsPlay) fsPlay.classList.remove('is-active');
    if (fsClose) fsClose.focus();
  }

  function close() {
    if (!fs || !isOpen) return;
    isOpen = false;
    fsVideo.pause();
    fs.classList.remove('is-open');
    fs.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mdj-vfs-open');
    if (!document.hidden) playMini();
  }

  root.addEventListener('click', function (ev) {
    if (closeBtn && closeBtn.contains(ev.target)) return;
    open();
  });

  root.addEventListener('keydown', function (ev) {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      open();
    }
  });

  if (fsClose) fsClose.addEventListener('click', close);

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && isOpen) close();
  });

  if (fsPlay) {
    fsPlay.addEventListener('click', function () {
      if (fsVideo.paused) {
        fsVideo.play().catch(function () {});
        fsPlay.classList.remove('is-active');
      } else {
        fsVideo.pause();
        fsPlay.classList.add('is-active');
      }
    });
  }

  if (fsMute) {
    fsMute.addEventListener('click', function () {
      fsVideo.muted = !fsVideo.muted;
      fsMute.classList.toggle('is-active', fsVideo.muted);
    });
  }

  if (fsVideo) {
    fsVideo.addEventListener('click', function () {
      if (fsVideo.paused) {
        fsVideo.play().catch(function () {});
        if (fsPlay) fsPlay.classList.remove('is-active');
      } else {
        fsVideo.pause();
        if (fsPlay) fsPlay.classList.add('is-active');
      }
    });
  }

  // ------------------------------------------------------------------
  // Buy bar
  // ------------------------------------------------------------------

  // Keep the player's price in step with the variant picker behind it.
  function syncPrice() {
    var scope = document.querySelector('.product__info-container') || document;
    var live = scope.querySelector(
      '.price__container .price-item--sale, .price__container .price-item--regular, .price .price-item'
    );
    if (live && fsPrice && live.textContent.trim()) {
      fsPrice.textContent = live.textContent.trim();
    }
    var comp = scope.querySelector('.price__container .price-item--regular s, .price__sale s');
    if (fsCompare && comp && comp.textContent.trim()) {
      fsCompare.textContent = comp.textContent.trim();
      fsCompare.hidden = false;
    }
  }

  function currentVariantId() {
    var input = document.querySelector(
      '.product__info-container product-form form [name="id"], product-form form [name="id"]'
    );
    return (input && input.value) || CFG.variantId;
  }

  function cartHost() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  if (fsAtc) {
    var atcDefault = fsAtc.textContent.trim();

    fsAtc.addEventListener('click', function () {
      var id = currentVariantId();
      if (!id) return;

      fsAtc.disabled = true;
      fsAtc.textContent = CFG.addingLabel || 'Adding…';

      var body = { items: [{ id: Number(id), quantity: 1 }] };
      var host = cartHost();
      if (host && typeof host.getSectionsToRender === 'function') {
        body.sections = host.getSectionsToRender().map(function (s) {
          return s.id;
        });
        body.sections_url = window.location.pathname;
      }

      var url =
        (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') +
        '.js';

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (data.status) throw new Error(data.description || 'Cart error');

          fsAtc.textContent = CFG.addedLabel || 'Added';

          // Bump the badge in the player without a round trip.
          if (fsCount) {
            var n = parseInt(fsCount.textContent, 10) || 0;
            fsCount.textContent = String(n + 1);
            fsCount.hidden = false;
          }

          if (window.PUB_SUB_EVENTS && window.publish) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'mdj-video',
              productVariantId: Number(id)
            });
          }
          if (host && typeof host.renderContents === 'function') {
            host.classList.remove('is-empty');
            host.renderContents(data);
          }

          setTimeout(function () {
            fsAtc.textContent = atcDefault;
            fsAtc.disabled = false;
            if (CFG.closeAfterAdd) close();
          }, 1400);
        })
        .catch(function (err) {
          fsAtc.textContent = CFG.errorLabel || 'Try again';
          fsAtc.disabled = false;
          setTimeout(function () {
            fsAtc.textContent = atcDefault;
          }, 2000);
          if (window.console) console.warn('[MDJ video]', err);
        });
    });
  }

  // Variant changes happen in the theme's own markup, so watch it.
  var info = document.querySelector('.product__info-container');
  if (info && 'MutationObserver' in window) {
    var t;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(syncPrice, 80);
    }).observe(info, { childList: true, subtree: true, attributes: true });
  }
  syncPrice();
})();
